declare global {
    interface Window {
        plugin: any;
    }
}

import { isFreeTier } from '@intigral/utils/lib/user';
import h, { getAppVersion, getAppName, isTVodProgram, getAvodConfiguration } from './common';
import user from './user';
import channelsUtil from '../helpers/channelsUtil';
import { playerTypes } from '../config/constants';
import { log } from '@intigral/utils';
import npawPlugin from '../singletons/npawPluginSingleton';
import { PROGRAM_TYPE } from '@intigral/constant';
import { avodHandler } from './avodHandler';
import cdnHandler from './CDNHandler';
import { getLastCSAISupplyParameters, getLastCSAIAdBreakUrls } from '../hooks/useCSAI';
import { isGuestUser } from '../config/guestMode';
import { playerSSAIManager } from '../utils/SSAI';
import { generateSupplyTagsForSSAI } from '../utils/supplyTags';

// Youbora Enums
const ybType = {
    MOVIE: 'Movie',
    EPISODE: 'Episode',
    TRAILER: 'Trailer',
};

const CUSTOM_LINEAR_TYPE = {
    LIVE: 'LIVE',
    REWIND: 'REWIND',
    STARTOVER: 'STARTOVER',
    VOD: 'VOD',
    TRAILER: 'TRAILER',
    CATCHUP: 'CATCHUP',
};

const appVersion = getAppVersion();
const appName = getAppName();

/**
 * Get view configuration options for Youbora
 * @param asset - The asset object
 * @param type - The type of the asset
 * @returns View configuration options
 */
const getViewConfigurationsOptions = () => {
    const userObj = user.getUserInfo();
    return {
        'user.name': userObj?.uid || userObj?.accountId || 'Guest',
        // 'user.name': user.getUsername(),
    };
};

/**
 * Get Youbora properties format
 * @param program - The program object
 * @param channel - The channel object
 * @param type - The type of the asset
 * @returns Youbora properties format
 */
const getYouboraProperitiesFormat = (program: any, channel: any, type: string) => {
    const d: any = {};
    try {
        if (type === playerTypes.LiveTV && program && channel) {
            d.type = 'Linear';
            d.guid = channel.guid;
            d.channelId = channel.channelID;
            d.title = program.title;
            if (channel.endTime) {
                d.duration = Math.round((program.endTime - program.startTime) / 1000);
            }
            if (program.year) {
                d.year = program.year;
            }
            if (program.ratings && program.ratings.length) {
                d.pRating = h.getGenreRating(program.ratings).toUpperCase();
            }
            if (channel.category && program?.category?.length) {
                d.category = channel?.category[0].toLowerCase();
            }
        } else if (
            type === playerTypes.Vod ||
            type === playerTypes.Trailer ||
            type === playerTypes.PlayTrailer ||
            type === playerTypes.Outstream
        ) {
            d.type = type === playerTypes.Outstream ? 'Outstream' : ybType.MOVIE;
            d.guid = program.guid;
            d.title = program?.programTitle?.en || program?.textEn || program?.title;
            d.duration = program.duration;
            d.pRating = program?.ratings?.toUpperCase();
            d.category = Array.isArray(program.genre)
                ? program.genre.map((g: string) => g.toLowerCase())
                : program?.genre?.toLowerCase() || 'unknown';
        }
        if (type === playerTypes.Catchup) {
            d.type = CUSTOM_LINEAR_TYPE.CATCHUP;
            d.guid = program?.guid;
            d.channelId = program?.channelId;
            d.title = program.title || program?.programTitle?.en || program?.textEn;
            d.pRating = program?.ratings?.toUpperCase();
            if (program?.endTime && program?.startTime) {
                d.duration = Math.round((program.endTime - program.startTime) / 1000);
            }
            if (program.genre) {
                d.category = Array.isArray(program.genre)
                    ? program.genre.map((g: string) => g.toLowerCase())
                    : program.genre.toLowerCase();
            }
        }

        if (program) {
            if (!d.year && (type === playerTypes.Outstream ? program.year !== undefined : program.year)) {
                d.year = program.year;
            }
        }
        if (program) {
            d.program = program.title;
        }
        if (type === playerTypes.Trailer) {
            d.type = ybType.TRAILER;
            d.duration = program.duration;
        }
    } catch (e) {
        log.error(`[Youbora] getYouboraProperitiesFormat() err: ${JSON.stringify(e)}`);
    }

    return {
        filename: d.guid || 'unknown',
        content_id: d.guid || 'unknown',
        channelId: d.channelId || null,
        content_metadata: {
            title: d.title || 'unknown',
            genre: d.category ?? 'unknown',
            year: d.year ?? 'unknown',
            duration: d.duration || 0,
            parental: d.pRating || 'F',
            program: d.program || 'unknown',
        },
        content_type: d.type || 'unknown',
        device: {
            manufacturer: user.getUserDevice().deviceMake,
            type: user.getUserDevice().deviceModel,
            year: user.getUserDevice().deviceOSVerion,
            firmware: user.getUserDevice().deviceName,
        },
    };
};

/**
 * Get custom data for Youbora
 * @param program - The program object
 * @param channel - The channel object
 * @param type - The type of the asset
 * @param vodType - The VOD type
 * @returns Custom data for Youbora
 */
const getCustomData = (
    program: any,
    channel: any,
    type: string,
    vodType: string,
    requestedUrl?: string,
    isAdsEnabledFromAvod?: boolean,
    isToPlayFromBookmark?: boolean,
    isMidroll?: boolean
) => {
    const _isLinear = type === playerTypes.LiveTV;
    const _isStartOver = false;
    const _purchaseType = vodType;
    const isOutstream = type === playerTypes.Outstream;

    let channelName = '';
    let paId = '';
    let listingId = '';
    let paGuid = ' ';
    let linearType;

    if (type === playerTypes.LiveTV && program && channel) {
        channelName = channel.channelTitle;
        paId = program.paId;
        listingId = program.listingId;
        paGuid = program.guid;

        if (_isStartOver) {
            linearType = CUSTOM_LINEAR_TYPE.STARTOVER;
        } else if (program && channelsUtil.isEarlier(program)) {
            linearType = CUSTOM_LINEAR_TYPE.REWIND;
        } else {
            linearType = CUSTOM_LINEAR_TYPE.LIVE;
        }
    } else if (type === playerTypes.Vod || isOutstream) {
        linearType = isOutstream ? 'OUTSTREAM' : CUSTOM_LINEAR_TYPE.VOD;
        paId = program.Id || program.guid;
        if (isOutstream) {
            paGuid = program.guid || 'NA';
            channelName = 'OUTSTREAM';
        }
    } else if (type === playerTypes.Catchup) {
        linearType = CUSTOM_LINEAR_TYPE.CATCHUP;
        paId = program.Id;
        channelName = program?.channelTitle ?? '';
        listingId = program?.listingId ?? '';
    }

    const tenantName = h.getTenantName();
    const uiLanguage = window.location.pathname.startsWith('/en') ? 'English' : 'Arabic';
    const videoEncoding = 'H.264';
    let vodPurchaseType = !_isLinear ? _purchaseType : '';

    if (type === playerTypes.Trailer) {
        linearType = CUSTOM_LINEAR_TYPE.TRAILER;
        vodPurchaseType = '';
    }

    const contentType = isOutstream ? 'NA' : _isLinear || type === playerTypes.Catchup ? 'linear' : 'vod';

    let isAdEligible = false;
    let adEligibilityDecisionVariables: Record<string, string | boolean> = {
        playerPlaybackType: isOutstream ? 'Outstream-Ad' : _isLinear ? 'CHANNEL' : 'ASSET',
        businessRuleEligibility: false,
        enableAVOD: false,
        isLinearAdsEnabled: false,
        isRewindAdsEnabled: false,
        isKidsMode: false,
        enableAdsKidsMode: false,
        isFreeTier: false,
        userOperator: '',
        isOperatorAdsEnabled: typeof isAdsEnabledFromAvod === 'boolean' ? isAdsEnabledFromAvod : false,
        isToPlayFromBookmark: typeof isToPlayFromBookmark === 'boolean' ? isToPlayFromBookmark : false,
        isContentAdEnabled: false,
    };
    let advertisingParameters: Record<string, string> = {};
    let adBreakData: Record<string, unknown> = {};
    let csaiURL = '';
    let adTechnology = '';
    const adType: string[] = isOutstream ? ['Outstream'] : isMidroll ? playerSSAIManager.youboraAdType : ['Pre-roll'];

    try {
        isAdEligible = Boolean(type === playerTypes.LiveTV ? channel?.adEnabled : program?.adEnabled);

        if (isAdEligible) {
            const avodConfig = (getAvodConfiguration?.() as Record<string, unknown>) || {};
            advertisingParameters = (getLastCSAISupplyParameters?.() as Record<string, string>) || {};
            if (isOutstream && advertisingParameters) {
                advertisingParameters.contentId = 'PENDING';
                advertisingParameters.contentGenre = 'PENDING';
                advertisingParameters.language = uiLanguage === 'English' ? 'en' : 'ar';
                advertisingParameters.contentRating = 'PENDING';
            }
            adBreakData = (avodHandler?.adData as Record<string, unknown>) || {};
            adTechnology = isMidroll ? 'SSAI' : isOutstream || avodHandler?.isCSAI ? 'CSAI' : 'SSAI';

            const urls = getLastCSAIAdBreakUrls?.() || [];
            csaiURL = urls.length > 0 ? urls[0] : '';

            // if (adType.length === 0 && isAdEligible && contentType === 'linear') {
            //     adType = ['Pre-roll'];
            // }

            const userInfo = user.getUserInfo();
            adEligibilityDecisionVariables = {
                playerPlaybackType: isOutstream ? 'Outstream-Ad' : _isLinear ? 'CHANNEL' : 'ASSET',
                businessRuleEligibility: false,
                enableAVOD: Boolean(avodConfig?.enableAVOD),
                isLinearAdsEnabled: Boolean(_isLinear),
                isRewindAdsEnabled: Boolean(type === playerTypes.Catchup),
                isKidsMode: Boolean(
                    program?.categoryType?.includes('kids') || (channel as any)?.category?.includes('kids')
                ),
                enableAdsKidsMode: Boolean(avodConfig?.enableAVODKidsMode),
                isFreeTier: Boolean(isFreeTier(userInfo)),
                userOperator: 'FREE_OP',
                isOperatorAdsEnabled: typeof isAdsEnabledFromAvod === 'boolean' ? isAdsEnabledFromAvod : true,
                isToPlayFromBookmark: typeof isToPlayFromBookmark === 'boolean' ? isToPlayFromBookmark : false,
                isContentAdEnabled: true,
            };
        }
    } catch (e) {
        log.error('[Youbora] Error computing ad fields:', e);
    }

    const customData = {
        profileName: user.getProfileName(),
        channelName: channelName || 'VOD',
        paID: paId || 'NA',
        ...(isOutstream ? { paGuid: paGuid || 'NA', playerPlaybackType: 'Outstream-Ad' } : {}),
        listingID: listingId || 'NA',
        playType: linearType,
        connType: 'NA',
        tenant: tenantName,
        language: isOutstream ? (uiLanguage === 'English' ? 'en' : 'ar') : uiLanguage,
        vodEncode: videoEncoding,
        userGuid: user.getUserGuId(),
        deviceGuid: user.getUserDeviceId(),
        userEnvironmentContext: user.getUserEnvironmentContext() || 'NA',
        group: user.getGroupId() || 'NA',
        vodPurchaseType: vodPurchaseType || 'NA',
        maxVideoQuality: 'SD',
        susbcriptionPlan: user.getUserTierName(),
        appVersion: appVersion,
        appName: appName,
        'Guest user': isGuestUser(user.getUserInfo()),
        isAdEligible,
        adEligibilityDecisionVariables: isOutstream
            ? { isGuestMode: Boolean(program?.isGuestMode) }
            : adEligibilityDecisionVariables,
        ...(isMidroll ? generateSupplyTagsForSSAI() : advertisingParameters),
        adBreakData,
        requestedURL: (isOutstream ? program?.requestedURL : (cdnHandler as any)._requestUrl || requestedUrl) || '',
        redirectedUrl: (isOutstream ? program?.redirectedUrl : (cdnHandler as any)._resolvedsmartlibUrl) || '',
        ...(type === playerTypes.LiveTV && { csaiURL }),
        adTechnology,
        adType,
        ...(isMidroll ? playerSSAIManager.youboraAdDetails || {} : {}),
        ...(isMidroll ? { adBreakData: playerSSAIManager.youboraAdBreakData } : {}),
        contentType,
    };

    return customData;
};

/**
 * Get media metadata options for Youbora
 * @param data - The data object
 * @param streamingUrl - The streaming URL
 * @param type - The type of the asset
 * @param vodType - The VOD type
 * @returns Media metadata options for Youbora
 */
const getMediaMetadataOptions = (
    data: any,
    streamingUrl: string,
    type: string,
    vodType: string,
    isAdsEnabledFromAvod?: boolean,
    isToPlayFromBookmark?: boolean,
    isMidroll?: boolean
) => {
    const program = type === playerTypes.LiveTV ? data.program : data;
    const channel = type === playerTypes.LiveTV ? data.channel : null;
    const isLive = type === playerTypes.LiveTV;
    const isOutstream = type === playerTypes.Outstream;

    const ybProperities = getYouboraProperitiesFormat(program, channel, type);

    let customData = getCustomData(
        program,
        channel,
        type,
        vodType,
        streamingUrl,
        isAdsEnabledFromAvod,
        isToPlayFromBookmark,
        isMidroll
    );

    customData = Object.assign(customData, {
        assetId:
            customData.playType === CUSTOM_LINEAR_TYPE.VOD ||
                customData.playType === CUSTOM_LINEAR_TYPE.TRAILER ||
                customData.playType === CUSTOM_LINEAR_TYPE.CATCHUP
                ? ybProperities.content_id
                : 'NA',
    });

    const intigralCustomData = {
        ...customData,
    };

    return {
        'content.title': ybProperities.content_metadata.title,
        'content.program': ybProperities.content_metadata.program,
        ...(isOutstream
            ? {
                'content.playbackType': 'VOD',
                'content.type': 'VOD',
            }
            : {}),
        cdnProvider: streamingUrl,
        'content.metadata': {
            filename: ybProperities.filename || 'unknown',
            content_id: ybProperities.content_id || 'unknown',
            genre: ybProperities.content_metadata.genre ?? 'unknown',
            year: ybProperities.content_metadata.year ?? 'unknown',
            parental: ybProperities.content_metadata.parental || 'F',
            channelId: ybProperities.channelId || null,
            intigral_custom_data: intigralCustomData,
            appVersion: appVersion,
            appName: appName,
        },
    };
};

/**
 * Get network options for Youbora
 * @param asset - The asset object
 * @param type - The type of the asset
 * @returns Network options for Youbora
 */
const getNetworkOptions = (asset: any, type: string) => {
    return {
        'network.ip': h.getUserIP(),
    };
};

/**
 * Get extra parameters for Youbora
 * @param data - The data object
 * @param type - The type of the asset
 * @param vodType - The VOD type
 * @returns Extra parameters for Youbora
 */
const getExtraParameters = (data: any, type: string, vodType: string, isMidroll: boolean = false) => {
    const program = type === playerTypes.LiveTV ? data.program : data;
    const channel = type === playerTypes.LiveTV ? data.channel : null;
    const customData = getCustomData(program, channel, type, vodType, null, null, null, isMidroll);
    const currentTier = user.getUserTierName();

    const isMovieOrSeries =
        program?.programType === PROGRAM_TYPE.MOVIE ||
        program?.programType === PROGRAM_TYPE.SERIES ||
        program?.programType === PROGRAM_TYPE.EPISODE;

    const isLinearOrCatchup =
        type === playerTypes.LiveTV ||
        type === playerTypes.Catchup ||
        customData.playType === playerTypes.LiveTV.toUpperCase() ||
        customData.playType === playerTypes.Catchup.toUpperCase();

    const isNA =
        type === playerTypes.Trailer ||
        type === playerTypes.PlayTrailer ||
        type === playerTypes.Outstream ||
        isLinearOrCatchup ||
        customData.playType === playerTypes.Trailer.toUpperCase() ||
        customData.playType === playerTypes.PlayTrailer.toUpperCase() ||
        customData.playType === 'OUTSTREAM';

    const isSVOD = !isNA && isMovieOrSeries;
    const isTVOD = !isNA && isMovieOrSeries && isTVodProgram(program);
    const isAVOD = !isNA && isMovieOrSeries && program?.adEnabled;

    const isLinearWithAds = isLinearOrCatchup && customData.isAdEligible;

    let dimension12 = isAVOD
        ? 'AVOD'
        : isTVOD
            ? 'TVOD'
            : isSVOD
                ? 'SVOD'
                : isLinearWithAds
                    ? 'LINEAR_ADVERT'
                    : isNA
                        ? 'NA'
                        : '';

    if (type === playerTypes.Outstream) {
        dimension12 = 'OUTSTREAM_AD';
    }

    return {
        'content.customDimension.1': type === playerTypes.Outstream ? '' : customData.channelName,
        'content.customDimension.2': type === playerTypes.Outstream ? 'OUTSTREAM_AD' : customData.playType,
        'content.customDimension.3': customData.connType,
        'content.customDimension.4': customData.tenant,
        'content.customDimension.5': customData.language,
        'content.customDimension.6': customData.vodEncode,
        'content.customDimension.7': user.getUserEnvironmentContext(),
        'content.customDimension.8': h.getYouboraIdentifier(),
        'content.customDimension.9': type === playerTypes.Outstream ? '' : 'SD',
        'content.customDimension.10': currentTier,
        ...(dimension12 ? { 'content.customDimension.12': dimension12 } : {}),
    };
};

/**
 * Get device options for Youbora
 * @param data - The data object
 * @param type - The type of the asset
 * @returns Device options for Youbora
 */
const getDeviceOptions = (data: any, type: string) => {
    return {
        'device.id': 27,
        'app.name': appName,
        'app.releaseVersion': appVersion,
    };
};

/**
 * Update Youbora analytics
 * @param asset - The asset object
 * @param streamingUrl - The streaming URL
 * @param type - The type of the asset
 * @param isTvod - The TVOD flag
 * @param isAdsEnabledFromAvod - Whether ads are enabled from useAvod (used for isOperatorAdsEnabled)
 * @param isToPlayFromBookmark - Whether playback is resuming from a bookmark (VOD resume)
 */
const updateYouboraAnalytics = (
    asset: any,
    streamingUrl: string,
    type: string,
    isTvod: string | boolean = 'NA',
    isAdsEnabledFromAvod?: boolean,
    isToPlayFromBookmark?: boolean,
    isMidroll: boolean = false
) => {
    try {
        const vodType = isTvod === 'NA' ? isTvod : isTvod ? 'TVOD' : 'SVOD';
        const options = {
            ...getViewConfigurationsOptions(),
            ...getMediaMetadataOptions(
                asset,
                streamingUrl,
                type,
                vodType,
                isAdsEnabledFromAvod,
                isToPlayFromBookmark,
                isMidroll
            ),
            ...getNetworkOptions(asset, type),
            ...getDeviceOptions(asset, type),
            ...getExtraParameters(asset, type, vodType, isMidroll),
        };

        npawPlugin.setAnalyticsOptions(options);
    } catch (error) {
        log.error('[Youbora] npaw-error', error);
    }
};

/**
 * Set Youbora plugin options
 * @param player - The player object
 * @param options - The options object
 */
/*
 * NPAW evaluates the adapter (`this.player.exports...`) inside its own fetch promise, so a null
 * player surfaces as an unhandled rejection rather than a catchable error here.
 */
const setYBPluginOptions = (player: any) => {
    if (!player) return;
    npawPlugin.registerAdapter(player, window.location.origin + '/js/7.0.9-bitmovin8-js-ads.json');
};

const setYBAdPluginOptions = (player: any) => {
    if (!player) return;
    npawPlugin.registerAdsAdapter(player, window.location.origin + '/js/7.0.9-bitmovin8-js-ads.json');
};

const removeYBAdapter = () => {
    npawPlugin.removeAdapter();
};

const removeYBAdsAdapter = () => {
    npawPlugin.removeAdsAdapter();
};

export { setYBPluginOptions, updateYouboraAnalytics, setYBAdPluginOptions, removeYBAdapter, removeYBAdsAdapter };
